import { Badge } from "@/components/ui/badge";
import { formatCurrency } from '@/lib/formatters';
import type { SaleConfig } from "@/types";
import { Button } from "@/components/ui/button";
import { Pencil,MoreVertical,Ban,PlayCircle } from "lucide-react";
import {
DropdownMenu,
DropdownMenuContent,
DropdownMenuItem,DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { SuspensionAuditHoverCard } from "@/features/catalog/suspension-audit-hover-card";

interface Tier {
  startQuantity: string;
  unitPriceCents: number;
}

interface SaleConfigListCompactProps {
  configs: SaleConfig[];
  onEdit?: (config: SaleConfig) => void;
  onSuspend?: (config: SaleConfig) => void;
  onResume?: (config: SaleConfig) => void;
}

export function SaleConfigListCompact({
  configs,
  onEdit,
  onSuspend,
  onResume,
}: SaleConfigListCompactProps) {
  if (!configs || configs.length === 0) {
    return (
      <p className="text-sm text-muted-foreground italic bg-muted/20 p-4 rounded-md text-center border border-dashed">
        No sale configurations found.
      </p>
    );
  }

  return (
    <div className="grid gap-3">
      {configs.map((config) => {
        const tiers: Tier[] = config.pricingScheme?.tiers ?? [];
        const isTiered = !!config.pricingScheme;

        let simplePriceCents: number | null | undefined =
          config.flatPriceCents ??
          (config.pricingScheme?.mode === "SIMPLE"
            ? config.pricingScheme.flatPriceCents
            : null);

        if (
          (simplePriceCents === null || simplePriceCents === undefined) &&
          isTiered &&
          tiers.length === 1
        ) {
          const singleTier = tiers[0];
          const startQty = Number(singleTier.startQuantity);
          if (!isNaN(startQty) && startQty <= 1.0001) {
            simplePriceCents = singleTier.unitPriceCents;
          }
        }

        const isEffectiveSimple =
          simplePriceCents !== null && simplePriceCents !== undefined;
        const displayUom =
          config.sellUom?.shortForm === "pc" ? "ea" : config.sellUom?.shortForm;
        const unitLabel = displayUom ? `/${displayUom}` : "";

        return (
          <div
            key={config.id}
            className={`group border rounded-md bg-card shadow-sm overflow-hidden text-left transition-all ${
              config.isSuspended
                ? "border-red-200 dark:border-red-900/40 bg-red-50/10 dark:bg-red-950/20"
                : ""
            }`}
          >
            <div
              className={`p-2 flex items-center justify-between border-b ${
                config.isSuspended
                  ? "bg-red-50/30 dark:bg-red-900/20"
                  : "bg-muted/30"
              }`}
            >
              <div className="grid gap-0.5 max-w-[65%]">
                <div className="flex items-center gap-2 overflow-hidden">
                  <span
                    className="text-sm font-semibold truncate"
                    title={config.name}
                  >
                    {config.name}
                  </span>
                  {config.isSuspended && (
                    <SuspensionAuditHoverCard
                      id={config.id}
                      type="sale-config"
                      label="Suspended"
                    />
                  )}
                </div>
                <span className="text-[10px] text-muted-foreground font-mono truncate">
                  SKU: {config.sku}
                </span>
              </div>
              <div className="flex items-center gap-1 text-right shrink-0">
                {isEffectiveSimple ? (
                  <span className="text-sm font-bold text-primary whitespace-nowrap mr-1">
                    {formatCurrency(simplePriceCents as number)}
                  </span>
                ) : isTiered ? (
                  <Badge
                    variant="outline"
                    className="text-[10px] px-1.5 h-5 bg-background truncate max-w-[80px] mr-1"
                  >
                    {config.pricingScheme?.label}
                  </Badge>
                ) : (
                  <span className="text-sm font-bold text-primary whitespace-nowrap mr-1">
                    -
                  </span>
                )}

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground"
                    >
                      <MoreVertical className="h-3.5 w-3.5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-32">
                    {onEdit && (
                      <DropdownMenuItem
                        onClick={() => onEdit(config)}
                        className="gap-1.5"
                      >
                        <Pencil className="h-4 w-4" />
                        Edit
                      </DropdownMenuItem>
                    )}

                    {config.isSuspended
                      ? onResume && (
                          <DropdownMenuItem
                            onClick={() => onResume(config)}
                            className="gap-1.5"
                          >
                            <PlayCircle className="h-4 w-4" />
                            Resume
                          </DropdownMenuItem>
                        )
                      : onSuspend && (
                          <DropdownMenuItem
                            onClick={() => onSuspend(config)}
                            className="gap-1.5 text-red-500 hover:text-red-600 focus:text-red-600"
                          >
                            <Ban className="h-4 w-4" />
                            Suspend
                          </DropdownMenuItem>
                        )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {config.isSuspended && config.suspendReason && (
              <div className="px-3 py-1.5 bg-red-50/50 dark:bg-red-950/20 border-b text-[10px] text-red-800 dark:text-red-400 italic">
                Reason: &quot;{config.suspendReason}&quot;
              </div>
            )}

            {isTiered && !isEffectiveSimple && tiers.length > 0 && (
              <div className="p-0">
                <div className="w-full text-xs">
                  {tiers.map((tier, idx) => (
                    <div
                      key={idx}
                      className="flex justify-between items-center py-1.5 px-3 border-b last:border-0 hover:bg-muted/10 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-muted-foreground w-4 text-center">
                          {idx + 1}.
                        </span>
                        <span>
                          Qty: {tier.startQuantity}+
                          <span className="text-muted-foreground ml-0.5 lowercase">
                            {config.sellUom?.shortForm ?? "units"}
                          </span>
                        </span>
                      </div>
                      <span className="font-medium">
                        {formatCurrency(tier.unitPriceCents)}
                        <span className="text-muted-foreground font-normal">
                          {unitLabel}
                        </span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(isEffectiveSimple || (!isTiered && !isEffectiveSimple)) && (
              <div className="px-3 py-2 text-xs text-muted-foreground flex justify-between items-center">
                <span>Sold by: {config.sellUom?.name ?? "Unit"}</span>
                {config.minQuantity > 1 && (
                  <span>Min Qty: {config.minQuantity}</span>
                )}
                {isEffectiveSimple && (
                  <span className="font-medium text-foreground">
                    {formatCurrency(simplePriceCents as number)}
                    <span className="text-muted-foreground font-normal">
                      {unitLabel}
                    </span>
                  </span>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
